import type { AsyncDataOptions, NuxtApp } from '#app'

import type {
  CmsFooterQuery,
  CmsPaymentIconsQuery,
  Page,
  Seo,
  TrustCertificatesCollection,
  TrustTeaserCarouselsCollection,
} from '../../shared/@types/generated/cms/graphql-schema-types'
import type { CustomFaqLotteries, CustomFaqScratchcards } from '../../shared/types'

type Options<T> = Pick<AsyncDataOptions<T>, 'immediate' | 'lazy'>

const isExpired = (expirationDate: number) => new Date(expirationDate).getTime() < Date.now()

const getCachedData = (key: string, nuxtApp: NuxtApp) => {
  const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key]
  if (data && isExpired(data.expirationDate)) {
    try {
      // oxlint-disable-next-line typescript/no-dynamic-delete
      delete nuxtApp.payload.data[key]
      return
    } catch {
      /* empty */
    }
  }
  if (!data) return
  return data
}

const sanitize = <T>(input: T) => JSON.parse(JSON.stringify(input).replace(/\\"/g, '“')) as T

export const transform = <T>(input: T): T & { expirationDate: number } => {
  const DAYS_UNTIL_EXPIRATION = 7
  const now = new Date()
  const expirationDate = now.setDate(now.getDate() + DAYS_UNTIL_EXPIRATION)

  // Prevents issues with not closed quotes in JSON content
  return { ...sanitize(input), expirationDate }
}

export const useContent = () => {
  const licensedTerritory = useLicensedTerritory({ case: 'snake' })
  const { currentLocale } = useLanguageUtils()

  const currentLocaleSnake = computed(() => currentLocale.value.replace(/-/g, '_'))

  const { log: datadogLog } = useDatadog()
  const {
    app: { cdnURL },
  } = useRuntimeConfig()
  const isServer = getIsServer()

  const getBaseUrl = () => {
    const basePath = isServer ? `${useRequestURL().origin}` : cdnURL
    return `${basePath}/__content__`
  }

  const getPathByContentId = (collection: string, id: string) =>
    `${getBaseUrl()}/${licensedTerritory}/${currentLocaleSnake.value}/${collection}/${id}.json`

  const getContentById = <ReturnType>(
    collection: string,
    id: string,
    options: Options<ReturnType> = {},
  ) =>
    useAsyncData(
      `${collection}/${id}/${licensedTerritory}/${currentLocaleSnake.value}`,
      (_nuxtApp, { signal }) =>
        $fetch(getPathByContentId(collection, id), { signal })
          .catch((error) => {
            if (error?.response?.status === 404) return

            datadogLog('error', {
              component: 'useContent.ts',
              error,
              message: `[content] Failed to getContentById(${collection}, ${id})`,
            })
          })
          .then((data) => data as ReturnType),
      {
        getCachedData,
        lazy: getIsClient(),
        transform: transform<ReturnType>,
        ...options,
      },
    )

  const getUniqueContent = <ReturnType>(collection: string, withLicensedTerritory = true) => {
    const territoryPath = withLicensedTerritory ? `${licensedTerritory}/` : ''
    const filePath = `${getBaseUrl()}/${territoryPath}${currentLocaleSnake.value}/${collection}.json`

    return useAsyncData(
      `${collection}/${licensedTerritory}/${currentLocaleSnake.value}`,
      (_nuxtApp, { signal }) =>
        $fetch(filePath, { signal })
          .catch((error) => {
            if (error?.response?.status === 404) return

            datadogLog('error', {
              component: 'useContent.ts',
              error,
              message: `[content] Failed to getUniqueContent(${collection})`,
            })
          })
          .then((data) => data as ReturnType),
      {
        getCachedData,
        lazy: getIsClient(),
        transform: transform<ReturnType>,
      },
    )
  }

  const getFaq = (id: string, options?: Options<Page>) =>
    getContentById<CustomFaqLotteries | CustomFaqScratchcards>('faqs', id, options)

  const getFooter = () => getUniqueContent<CmsFooterQuery['footerCollection']>('footer')

  const getPage = (id: string, options?: Options<Page>) => {
    const normalizedId = id.startsWith('/') ? id.slice(1) : id
    return getContentById<Page>('pages', normalizedId, options)
  }

  const fetchPage = (id: string) => $fetch<Page>(getPathByContentId('pages', id)).then(sanitize)

  const getPaymentIcons = () =>
    getUniqueContent<CmsPaymentIconsQuery['paymentIconCollection']>('payment-icons')

  const getPrizes = (id: string, options?: Options<Page>) =>
    getContentById<Page>('prizes', id, options)

  const getRules = (id: string, options?: Options<Page>) =>
    getContentById<Page>('rules', id, options)

  const getSeo = () => getUniqueContent<{ items: Seo[] }>('seo')

  const getTrustTeaserCarousels = () =>
    getUniqueContent<TrustTeaserCarouselsCollection>('trust-teaser-carousels')

  const getTrustCertificates = () =>
    getUniqueContent<TrustCertificatesCollection>('trust-certificates')

  return {
    fetchPage,
    getFaq,
    getFooter,
    getPage,
    getPaymentIcons,
    getPrizes,
    getRules,
    getSeo,
    getTrustCertificates,
    getTrustTeaserCarousels,
  }
}
